/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Snigdha Dhanireddy <snigdha.dhanireddy@mroads.com>, June 2023
 * @flow
 */

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import useStyles from './useStyles';
import { Typography, Button, IconButton, Box, Grid, Card } from '@material-ui/core';
import { HighlightOff as HighlightOffIcon, SmsOutlined as SmsIcon, } from '@material-ui/icons';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ServerLookup from '../ServerLookup';

const AssignTable = ({
  goToWaitlistScreen,
  onAssignTableClick,
  selectedBooking,
  onEmployeeSelection,
  employees,
  activeTableAction,
  updatedShapes,
  onClickTable,
  onProceedWithoutTable,
}) => {
  const classes = useStyles();
  const { labelsData } = useSelector(state => state);
  const { labels: strings } = labelsData;
  const [proceedWithoutTableNumber, setProceedWithoutTableNumber] = useState(false);

  const handleChecked = event => {
    setProceedWithoutTableNumber(event.target.checked);
    onProceedWithoutTable && onProceedWithoutTable(event.target.checked);
  };

  const onClickTableCard = table => {
    if (table.stationId && activeTableAction?.assignTable) {
      onClickTable && onClickTable(table);
    }
  };

  const onAssign = () => {
    onAssignTableClick();
  };

  const isAssignDisabled = () => {
    if (!activeTableAction?.assignTable) return false;
    const filteredShapes = Array.from(updatedShapes.values()).filter(it => it.stationId && it.selected);
    return filteredShapes.length === 0;
  };

  const guestLabelValues = [
    { id: 1, key: strings.ASSIGN_NAME, value: selectedBooking.name, class: classes.guestValue },
    { id: 2, key: strings.ASSIGN_CONTACT, value: '+' + selectedBooking.countryCode + ' ' + selectedBooking.phoneNumber, class: classes.guestValue, showIcon: true, },
    { id: 3, key: strings.ASSIGN_EMAIL, value: selectedBooking.emailAddress, class: classes.guestEmail },
    { id: 4, key: strings.ASSIGN_NOTES, value: selectedBooking.note, class: classes.guestEmail },
  ];

  const tables = Array.from(updatedShapes.values()).filter(it => it.stationId);

  return (
    <Box className={classes.container} height={'100vh'}>
      <Box height={'7vh'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} className={classes.header}>
        <Typography variant='h6' className={classes.title}>{strings.ASSIGN_TABLE}</Typography>
        <IconButton className={classes.closeImg} onClick={goToWaitlistScreen}>
          <HighlightOffIcon />
        </IconButton>
      </Box>
      <Box p={1.5} height={'calc(93vh - 32px)'} overflow={'scroll'}>
        <Box className={classes.cardContainer}>
          <Box height={330} overflow={'scroll'} mb={0.5}>
            <Grid container>
              {tables.map(table =>
                <Grid item xs={6} sm={6} md={4} lg={3} key={`id-${table.id}-${table.stationId}`}>
                  <Card
                    elevation={0}
                    className={cn(classes.tableCard, { [classes.selectedTableCard]: table.selected })}
                    onClick={() => onClickTableCard(table)}
                    style={{ cursor: activeTableAction?.assignTable ? 'pointer' : 'not-allowed' }}
                  >
                    <Typography variant='body2' className={classes.tableText}>{table.stationNumber}</Typography>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={proceedWithoutTableNumber}
                color="primary"
                onChange={handleChecked}
                name="proceedWithoutTableNumber"
              />
            }
            label={strings.ASSIGN_PROCEED}
            variant='body2'
          />
        </Box>
        <ServerLookup onEmployeeSelection={onEmployeeSelection} employees={employees} />
        <Box padding={2} height={270} className={classes.guestContainer}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Typography variant='body1' className={classes.guestDetails}>{strings.ASSIGN_GUEST_DETAILS}</Typography>
            <Box className={classes.partySizeBox}>{selectedBooking?.partySize}</Box>
          </Box>
          {guestLabelValues.map(each => {
            if (each.showIcon) {
              return (
                <Box key={`key-${each.id}-${each.key}`}>
                  <Typography variant='body2' className={classes.guestLabel}>{each.key}</Typography>
                  <Box display={'flex'} justifyContent={'space-between'}>
                    <Typography className={each.class}>{each.value}</Typography>
                    <IconButton size='medium' className={classes.smsIcon}>
                      <SmsIcon fontSize='inherit' />
                    </IconButton>
                  </Box>
                </Box>
              )
            }
            return (
              <Box key={`key-${each.id}-${each.key}`}>
                <Typography variant='body2' className={classes.guestLabel}>{each.key}</Typography>
                <Typography className={each.class}>{each.value}</Typography>
              </Box>
            )
          })}
        </Box>
        <Box display={'flex'} flexDirection={'column'}>
          <Button
            variant="contained"
            color="primary"
            onClick={onAssign}
            className={classes.button}
            disabled={isAssignDisabled()}
          >
            {strings.ASSIGN_BUTTON_LABEL}
          </Button>
          <Button
            variant="outlined"
            color='primary'
            onClick={goToWaitlistScreen}
            className={classes.secondaryButton}
          >
            {strings.ASSIGN_CANCEL}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AssignTable;

AssignTable.propTypes = {
  goToWaitlistScreen: PropTypes.func,
  onAssignTable: PropTypes.func,
  selectedBooking: PropTypes.object,
  onEmployeeSelection: PropTypes.func,
  employees: PropTypes.array,
};

AssignTable.defaultProps = {
  goToWaitlistScreen: () => { },
  onAssignTable: () => { },
  selectedBooking: {},
  onEmployeeSelection: () => { },
  employees: [],
};
